<!--
  StatementConfirmCard.vue
  콜봇 월 정산 · 정산 탭 오른쪽 '이 달 확정' 카드 — 목업 Detail.dc.html 의 aside.

  - 검토 필요(review): 남은 차단 경고마다 확인 체크박스(모두 체크해야 확정이 켜진다), 저장 메모, [저장](입력이 있을 때),
    [다시 계산](마감 상태 패널의 목록 앞 버튼이나 v70 이전 줄에 이미 있으면 숨긴다 — recalcInTodo), primary '{청구 합계}원으로 확정'과 그
    아래 막는 이유 한 줄(결제가 걸린 달이면 '확정하면 admin-reseller 수납 대상이 됩니다.'를 붙인다). 동작은 모두
    useStatementReview 가 한다(저장 확인창·두 번 묻는 확정·expected_rev/expected_total·409 처리). 금액 입력은 정산서 표의 '고치기'다.
  - 확정(confirmed): [재수정](셸의 재수정 확인창)과 한 줄 안내 — 결제가 걸린 달(in·null)이면 수납 문장(받은 돈 그대로 ·
    admin-reseller 차액)을 본문에 보이고, 전체 안내(confirmedReopenHintText)는 정보 아이콘 버튼의 툴팁(마우스·키보드).
  - 그 밖의 상태는 그리지 않는다(부모가 v-if).

  props: review(useStatementReview) · detail · status
  emits: open-reopen
  exposes: focusRecalculate() — 이 카드에 [다시 계산]이 없으면 false(부모가 패널로 보낸다) · focusChecklist()
-->
<template>
  <section class="confirm-card" :aria-labelledby="`${uid}-title`">
    <h2 :id="`${uid}-title`" class="confirm-card__title">이 달 확정</h2>

    <template v-if="status === 'review'">
      <fieldset
        v-if="review.blocking.value.length"
        ref="checklistEl"
        class="confirm-card__checks"
        :class="{ 'is-flash': flash === 'checklist' }"
      >
        <legend class="confirm-card__legend">
          그대로 확정하려면 아래 {{ review.blocking.value.length }}건을 확인합니다
        </legend>
        <a-checkbox
          v-for="w in review.blocking.value"
          :key="w.code"
          class="confirm-card__check"
          :checked="review.ackSet.value.has(w.code)"
          :disabled="!!review.busy.value"
          @update:checked="(checked) => review.toggleAck(w.code, checked)"
        >
          <!-- a-checkbox drops a title attribute — the warning code goes on the label text instead -->
          <span :title="w.code">{{ warningText(w, { compact: true, otherCost }) }}</span>
        </a-checkbox>
      </fieldset>
      <!-- No blocking warning left — the state panel title already says so; keep only the focus anchor -->
      <span v-else ref="checklistEl" class="confirm-card__anchor" aria-hidden="true"></span>

      <label class="confirm-card__memo">
        <span>메모 (선택)</span>
        <a-textarea
          :value="review.memo.value"
          :maxlength="500"
          :auto-size="{ minRows: 2, maxRows: 4 }"
          placeholder="마감 이력에 남습니다"
          :disabled="!!review.busy.value"
          @update:value="review.setMemo"
        />
      </label>

      <div
        v-if="showRecalc || review.dirty.value || review.legacy.value"
        ref="recalcEl"
        class="confirm-card__row"
        :class="{ 'is-flash': flash === 'recalc' }"
      >
        <a-tooltip v-if="review.dirty.value || review.legacy.value" :title="review.saveBlockedReason.value || undefined">
          <span class="confirm-card__btn-wrap">
            <a-button
              :loading="review.busy.value === 'save'"
              :disabled="!!review.saveBlockedReason.value || (!!review.busy.value && review.busy.value !== 'save')"
              @click="review.onSave"
            >
              저장
            </a-button>
          </span>
        </a-tooltip>
        <a-tooltip
          v-if="showRecalc"
          title="요율을 고친 뒤나 통화가 끝난 뒤 지금 값으로 다시 만듭니다. 사람이 넣은 항목 금액은 그대로 둡니다."
        >
          <a-button
            :loading="review.busy.value === 'recalc'"
            :disabled="!!review.busy.value && review.busy.value !== 'recalc'"
            @click="review.recalculate"
          >
            <ReloadOutlined v-if="review.busy.value !== 'recalc'" />
            다시 계산
          </a-button>
        </a-tooltip>
        <span v-if="review.dirty.value" class="confirm-card__dirty">저장하지 않은 입력이 있습니다</span>
      </div>

      <a-button
        type="primary"
        class="yellow-button confirm-card__primary"
        block
        :loading="review.busy.value === 'confirm'"
        :disabled="!!review.confirmBlockedReason.value || (!!review.busy.value && review.busy.value !== 'confirm')"
        @click="review.onConfirm"
      >
        {{ confirmLabel }}
      </a-button>
      <p class="confirm-card__reason">{{ reasonText }}</p>
    </template>

    <template v-else-if="status === 'confirmed'">
      <a-button class="confirm-card__reopen" @click="emit('open-reopen')">
        <EditOutlined />
        재수정
      </a-button>
      <p class="confirm-card__reason">
        재수정하면 검토 필요로 돌아갑니다.{{ paymentMonth ? ` ${REOPEN_PAYMENT_HINT_TEXT}` : '' }}
        <a-tooltip :title="reopenHint" :trigger="['hover', 'focus']">
          <button type="button" class="confirm-card__info" :aria-label="`재수정 안내: ${reopenHint}`">
            <InfoCircleOutlined aria-hidden="true" />
          </button>
        </a-tooltip>
      </p>
    </template>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
import { EditOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons-vue';

import { formatNumber, isNumber } from '../../service/settlementFormat';
import {
  REOPEN_PAYMENT_HINT_TEXT,
  confirmedReopenHintText,
  isInternalDetail,
  warningText,
} from '../../service/settlementRules';
import { isPaymentMonthScope, paymentScopeOf } from '../../service/settlementPayment';

const props = defineProps({
  /** useStatementReview() 의 반환값 */
  review: { type: Object, required: true },
  /** 정산 상세(요청 계약 2) */
  detail: { type: Object, required: true },
  /** statementStatusOf(detail) */
  status: { type: String, required: true },
});

const emit = defineEmits(['open-reopen']);

const uid = `st-confirm-${getCurrentInstance()?.uid ?? 0}`;

const READY_TEXT = '확정하면 이 금액이 이 달 확정 정산 금액이 됩니다.';
/** Payment month (in · null) — what confirming starts (mockup wording) */
const PAYMENT_READY_TEXT = '확정하면 admin-reseller 수납 대상이 됩니다.';

/** Cold-transfer short name carries the other cost ('상담사 직접 연결 시간 빠짐(기타 원가 n원)') */
const otherCost = computed(() => (isNumber(props.detail.cost?.split?.other) ? props.detail.cost.split.other : null));

/** Payment is tied to this month (payment_scope in, or unreadable = null) */
const paymentMonth = computed(() => isPaymentMonthScope(paymentScopeOf(props.detail)));

/** [다시 계산] once per screen — hidden here when a todo item in the state panel already offers it */
const showRecalc = computed(() => !props.review.recalcInTodo.value);

/** Line under the confirm button — why it is off, or what confirming does */
const reasonText = computed(() => {
  const r = props.review;
  const blocked = r.confirmBlockedReason.value;
  // Legacy / empty amounts: the reason alone (the payment line would only distract from what to do first)
  if (blocked && (r.legacy.value || r.anyEmpty.value)) return blocked;
  if (paymentMonth.value) return blocked ? `${blocked} ${PAYMENT_READY_TEXT}` : PAYMENT_READY_TEXT;
  return blocked || READY_TEXT;
});

/** '{청구 합계}원으로 확정' — amounts typed in the statement table, VAT included */
const confirmLabel = computed(() => {
  const t = props.review.totals.value;
  // A v70-legacy statement has no new items yet — its typed total means nothing until recalculated
  if (props.review.legacy.value || !t || !isNumber(t.totalWithVat)) return '이 달 확정';
  return `${formatNumber(t.totalWithVat)}원으로 확정`;
});

const confirmedTotal = computed(() => {
  if (isInternalDetail(props.detail)) return 0;
  const st = props.detail.statement?.total;
  return isNumber(st) ? st : props.detail.charge?.total ?? null;
});
const reopenHint = computed(() => confirmedReopenHintText(confirmedTotal.value, paymentScopeOf(props.detail)));

// ---------------------------------------------------------------------------
// Focus — called after a resolution / intent (scroll + short highlight)
// ---------------------------------------------------------------------------

const checklistEl = ref(null);
const recalcEl = ref(null);
const flash = ref('');
let flashTimer = null;

function flashAt(key, el) {
  const node = el?.$el ?? el;
  if (!node) return;
  node.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  flash.value = key;
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => {
    flash.value = '';
  }, 2200);
}

/** @returns {boolean} false when this card has no [다시 계산] (the state panel's todo item has it) */
function focusRecalculate() {
  if (!showRecalc.value) return false;
  flashAt('recalc', recalcEl.value);
  const buttons = recalcEl.value?.querySelectorAll?.('button');
  buttons?.[buttons.length - 1]?.focus({ preventScroll: true });
  return true;
}

function focusChecklist() {
  flashAt('checklist', checklistEl.value);
}

onBeforeUnmount(() => clearTimeout(flashTimer));

defineExpose({ focusRecalculate, focusChecklist });
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

@include yellow-button;

@mixin flash-target {
  border-radius: 8px;
  transition: box-shadow 0.3s ease, background-color 0.3s ease;

  &.is-flash {
    background-color: $st-accent-bg;
    box-shadow: 0 0 0 2px $st-accent-border;
  }
}

.confirm-card {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  color: $st-text;

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }

  &__checks {
    @include flash-target;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    border: 0;
    min-width: 0;
  }

  &__legend {
    margin-bottom: 4px;
    font-size: 13px;
    color: $st-text-secondary;
  }

  &__check {
    margin-inline-start: 0 !important;
    font-size: 14px;
  }

  &__anchor {
    display: block;
  }

  &__memo {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;
    font-size: 13px;
    color: $st-text-secondary;
  }

  &__row {
    @include flash-target;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  &__btn-wrap {
    display: inline-block;
  }

  &__dirty {
    font-size: 12px;
    font-weight: 600;
    color: $st-warning;
  }

  &__primary {
    height: 44px;
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  &__reopen {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__info {
    @include st-info-btn;
  }

  &__reason {
    margin: -6px 0 0;
    font-size: 12.5px;
    line-height: 1.6;
    color: $st-muted;
  }
}
</style>
